/**
 * UI-3: Entity Event Service
 *
 * Frontend client for fetching entity events from existing backend endpoints.
 * Uses GET /ops/events and GET /activity-ledger — NO new backend endpoints.
 *
 * Data flow:
 *   1. Fetch events from activity ledger (filtered by entity)
 *   2. Frontend replay derives current state + timeline + transitions
 *   3. Cursor pagination via offset (default: 100 events)
 */

import { API_BASE } from '@/config/api';
import type {
  TimelineEvent,
  TimelinePageResult,
  PageCursor,
} from './entityEventTypes';

const DEFAULT_LIMIT = 100;

// ─── Activity Ledger fetch (existing endpoint) ─────────────────────────────

interface LedgerFilters {
  room?: string;
  stayId?: string;
  type?: string;
  entityId?: string;
  entityType?: string;
  limit?: number;
  from?: string;
  to?: string;
}

async function fetchLedgerEvents(filters: LedgerFilters): Promise<TimelineEvent[]> {
  const params = new URLSearchParams();

  if (filters.room) params.append('room', filters.room);
  if (filters.stayId) params.append('stayId', filters.stayId);
  if (filters.type) params.append('type', filters.type);
  if (filters.entityId) params.append('entityId', filters.entityId);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);

  const url = `${API_BASE}/activity-ledger${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch events: ${response.statusText}`);
  }

  const data = await response.json();
  const records = data.activities || [];

  return records.map(mapActivityToTimelineEvent);
}

// ─── OPS Events fetch (existing endpoint, richer event detail) ──────────────

async function fetchOpsEvents(filters: {
  entityId?: string;
  entityType?: string;
  eventType?: string;
  limit?: number;
}): Promise<TimelineEvent[]> {
  const params = new URLSearchParams();

  if (filters.entityId) params.append('entityId', filters.entityId);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.eventType) params.append('eventType', filters.eventType);
  if (filters.limit) params.append('limit', filters.limit.toString());

  const url = `${API_BASE}/ops/events${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ops events: ${response.statusText}`);
  }

  const data = await response.json();
  return (data.events || []).map(mapOpsEventToTimeline);
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Fetch timeline events for a room entity.
 * Uses activity ledger filtered by room number.
 */
export async function fetchRoomEvents(
  roomNumber: string,
  limit = DEFAULT_LIMIT,
  cursor?: PageCursor,
): Promise<TimelinePageResult> {
  const allEvents = await fetchLedgerEvents({
    room: roomNumber,
    limit: limit + (cursor?.offset ?? 0) + 1, // fetch enough to check hasMore
  });

  const offset = cursor?.offset ?? 0;
  const sliced = allEvents.slice(offset, offset + limit);
  const hasMore = allEvents.length > offset + limit;

  return {
    events: sliced,
    total: allEvents.length,
    hasMore,
    nextCursor: hasMore ? { offset: offset + limit } : undefined,
  };
}

/**
 * Fetch timeline events for a guest entity.
 * Uses activity ledger filtered by guest name / stay.
 */
export async function fetchGuestEvents(
  guestId: string,
  limit = DEFAULT_LIMIT,
  cursor?: PageCursor,
): Promise<TimelinePageResult> {
  // For guest views, query by stayId (guestId is often the stayId in the system)
  // Also try fetching from ops events by entityId
  const [ledgerEvents, opsEvents] = await Promise.all([
    fetchLedgerEvents({ stayId: guestId, limit: 200 }),
    fetchOpsEvents({ entityId: guestId, entityType: 'GUEST', limit: 200 }).catch(() => [] as TimelineEvent[]),
  ]);

  // Merge and deduplicate by id, prefer ops events (richer data)
  const merged = mergeAndDeduplicate(ledgerEvents, opsEvents);
  const sorted = merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const offset = cursor?.offset ?? 0;
  const sliced = sorted.slice(offset, offset + limit);
  const hasMore = sorted.length > offset + limit;

  return {
    events: sliced,
    total: sorted.length,
    hasMore,
    nextCursor: hasMore ? { offset: offset + limit } : undefined,
  };
}

/**
 * Fetch timeline events for a service request entity.
 * Uses ops events filtered by entityId.
 */
export async function fetchRequestEvents(
  requestId: string,
  limit = DEFAULT_LIMIT,
  cursor?: PageCursor,
): Promise<TimelinePageResult> {
  const [ledgerEvents, opsEvents] = await Promise.all([
    fetchLedgerEvents({ entityId: requestId, limit: 200 }),
    fetchOpsEvents({ entityId: requestId, limit: 200 }).catch(() => [] as TimelineEvent[]),
  ]);

  const merged = mergeAndDeduplicate(ledgerEvents, opsEvents);
  const sorted = merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const offset = cursor?.offset ?? 0;
  const sliced = sorted.slice(offset, offset + limit);
  const hasMore = sorted.length > offset + limit;

  return {
    events: sliced,
    total: sorted.length,
    hasMore,
    nextCursor: hasMore ? { offset: offset + limit } : undefined,
  };
}

/**
 * Fetch related events for causal context.
 * Given an event, fetch events with the same correlationId or nearby room/stay.
 */
export async function fetchRelatedEvents(
  event: TimelineEvent,
  limit = 20,
): Promise<TimelineEvent[]> {
  const promises: Promise<TimelineEvent[]>[] = [];

  // Fetch by room (nearby events in the same room)
  if (event.roomNumber) {
    promises.push(
      fetchLedgerEvents({
        room: event.roomNumber,
        limit,
      }),
    );
  }

  // Fetch by stayId (events in the same stay)
  if (event.stayId) {
    promises.push(
      fetchLedgerEvents({
        stayId: event.stayId,
        limit,
      }),
    );
  }

  const results = await Promise.all(promises);
  const flat = results.flat();

  // Deduplicate and exclude the source event
  const seen = new Set<string>([event.id]);
  const unique: TimelineEvent[] = [];
  for (const e of flat) {
    if (!seen.has(e.id)) {
      seen.add(e.id);
      unique.push(e);
    }
  }

  return unique
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .slice(0, limit);
}

// ─── Mapping ────────────────────────────────────────────────────────────────

function mapActivityToTimelineEvent(record: Record<string, unknown>): TimelineEvent {
  return {
    id: String(record.id ?? ''),
    timestamp: String(record.timestamp ?? ''),
    eventType: (record.eventType as TimelineEvent['eventType']) ?? null,
    type: String(record.type ?? ''),
    entityId: record.entityId ? String(record.entityId) : (record.id ? String(record.id) : null),
    entityType: (record.entityType as TimelineEvent['entityType']) ?? null,
    roomNumber: String(record.roomNumber ?? ''),
    stayId: String(record.stayId ?? ''),
    guestName: record.guestName ? String(record.guestName) : undefined,
    status: String(record.status ?? ''),
    source: (record.source as TimelineEvent['source']) ?? 'SYSTEM',
    label: String(record.eventType ?? record.type ?? ''),
    summary: record.category ? String(record.category) : null,
    actor: record.actor ? String(record.actor) : null,
    category: record.category ? String(record.category) : undefined,
    metadata: (record.metadata as Record<string, unknown>) ?? {},
  };
}

function mapOpsEventToTimeline(event: Record<string, unknown>): TimelineEvent {
  return {
    id: String(event.id ?? ''),
    timestamp: String(event.timestamp ?? ''),
    timestampMs: event.timestampMs ? Number(event.timestampMs) : undefined,
    eventType: (event.eventType as TimelineEvent['eventType']) ?? null,
    type: String(event.type ?? ''),
    entityId: event.entityId ? String(event.entityId) : null,
    entityType: (event.entityType as TimelineEvent['entityType']) ?? null,
    roomNumber: String(event.roomNumber ?? ''),
    stayId: String(event.stayId ?? ''),
    status: String(event.status ?? ''),
    source: (event.source as TimelineEvent['source']) ?? 'SYSTEM',
    label: String(event.label ?? event.eventType ?? ''),
    summary: event.summary ? String(event.summary) : null,
    actor: event.actor ? String(event.actor) : null,
    metadata: {},
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mergeAndDeduplicate(
  primary: TimelineEvent[],
  secondary: TimelineEvent[],
): TimelineEvent[] {
  const byId = new Map<string, TimelineEvent>();

  // Secondary first, then primary overwrites (primary = activity ledger = richer metadata)
  for (const e of secondary) byId.set(e.id, e);
  for (const e of primary) byId.set(e.id, e);

  return Array.from(byId.values());
}
